import { useRef, useCallback, useEffect } from 'react';
import Editor, { type OnMount, type BeforeMount } from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';
import type { ProjectFile } from '../../types/project';
import { registerMistLanguage, MIST_LANGUAGE_ID } from '../../editor/mistLanguage';
import { registerMistProviders } from '../../editor/mistProviders';
import { analyze } from '../../editor/diagnosticService';
import { analyzeFees, type LineFeeAnnotation } from '../../editor/feeEstimator';
import type { MistDiagnostic } from '../../editor/diagnosticTypes';
import { findNodeAtLine } from '../../editor/pviGraphService';
import { usePviStore } from '../../stores/usePviStore';
import { useDiagnosticStore } from '../../stores/useDiagnosticStore';

interface MonacoEditorProps {
  file: ProjectFile;
  onChange: (content: string) => void;
  onSave: () => void;
}

/** Track whether the Mist language has been registered globally. */
let mistRegistered = false;

/** Apply inline fee annotations as "after" decorations on each priced line. */
function applyFeeDecorations(
  editor: Parameters<OnMount>[0],
  monaco: typeof Monaco,
  annotations: LineFeeAnnotation[],
) {
  const ed = editor as unknown as {
    _feeDecorations?: string[];
    deltaDecorations: (old: string[], n: Monaco.editor.IModelDeltaDecoration[]) => string[];
  };

  // Group annotations by line and sum costs
  const lineMap = new Map<number, { total: number; labels: string[] }>();
  for (const a of annotations) {
    const entry = lineMap.get(a.line) ?? { total: 0, labels: [] };
    entry.total += a.cost;
    entry.labels.push(a.label);
    lineMap.set(a.line, entry);
  }

  const decorations: Monaco.editor.IModelDeltaDecoration[] = [];
  for (const [line, info] of lineMap) {
    const display = formatMicro(info.total);
    decorations.push({
      range: new monaco.Range(line, 1, line, 1),
      options: {
        after: {
          content: `  ⟡ ${display}`,
          inlineClassName: 'fee-inline-annotation',
        },
        isWholeLine: false,
      },
    });
  }

  ed._feeDecorations = ed.deltaDecorations(ed._feeDecorations ?? [], decorations);
}

function formatMicro(micro: number): string {
  if (micro >= 1_000_000) {
    const d = micro / 1_000_000;
    return `$${d % 1 === 0 ? d.toFixed(0) : d.toFixed(2)}`;
  }
  if (micro >= 1000) return `$0.${(micro / 1_000_000).toFixed(4).slice(2)}`;
  return `$0.${(micro / 1_000_000).toFixed(6).slice(2)}`;
}

function setMonacoMarkers(
  monaco: typeof Monaco,
  model: Monaco.editor.ITextModel,
  diagnostics: MistDiagnostic[],
) {
  const markers: Monaco.editor.IMarkerData[] = diagnostics.map(d => {
    const range = d.range;
    return {
      startLineNumber: range?.startLine ?? 1,
      startColumn: range?.startColumn ?? 1,
      endLineNumber: range?.endLine ?? range?.startLine ?? 1,
      endColumn: range?.endColumn ?? (range?.startColumn ?? 1) + 1,
      message: `[${d.code}] ${d.message}`,
      severity: d.severity === 'Error' ? monaco.MarkerSeverity.Error
        : d.severity === 'Warning' ? monaco.MarkerSeverity.Warning
        : monaco.MarkerSeverity.Info,
      source: 'mist',
      code: d.code,
    };
  });
  monaco.editor.setModelMarkers(model, 'mist', markers);
}

export function MonacoEditor({ file, onChange, onSave }: MonacoEditorProps) {
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isMist = file.language === MIST_LANGUAGE_ID;

  const setCompileResult = usePviStore(s => s.setCompileResult);
  const setHighlightedNodeId = usePviStore(s => s.setHighlightedNodeId);
  const highlightedLine = usePviStore(s => s.highlightedLine);

  const setDiagnostics = useDiagnosticStore(s => s.setDiagnostics);
  const setAnalyzing = useDiagnosticStore(s => s.setAnalyzing);

  // Graph→source: reveal and decorate line when graph sets highlightedLine
  useEffect(() => {
    if (!isMist || !editorRef.current || !monacoRef.current || highlightedLine === null) {
      // Clear decorations when nothing is highlighted
      if (editorRef.current && (editorRef.current as unknown as { _pviDecorations?: string[] })._pviDecorations) {
        const ed = editorRef.current as unknown as { _pviDecorations: string[]; deltaDecorations: (old: string[], n: unknown[]) => string[] };
        ed._pviDecorations = ed.deltaDecorations(ed._pviDecorations, []);
      }
      return;
    }
    const editor = editorRef.current;
    const monaco = monacoRef.current;

    editor.revealLineInCenter(highlightedLine);

    const ed = editor as unknown as { _pviDecorations?: string[]; deltaDecorations: (old: string[], n: unknown[]) => string[] };
    ed._pviDecorations = ed.deltaDecorations(ed._pviDecorations ?? [], [{
      range: new monaco.Range(highlightedLine, 1, highlightedLine, 1),
      options: {
        isWholeLine: true,
        className: 'pvi-highlighted-line',
        glyphMarginClassName: 'pvi-glyph-marker',
      },
    }]);
  }, [highlightedLine, isMist]);

  const runValidation = useCallback((content: string) => {
    if (!isMist || !monacoRef.current || !editorRef.current) return;

    setAnalyzing(true);

    const { result, pviResult } = analyze({ source: content, fileName: file.name });
    const model = editorRef.current.getModel();
    if (model) {
      setMonacoMarkers(monacoRef.current, model, result.diagnostics);
    }

    // Update diagnostic store (single source of truth)
    setDiagnostics(result.diagnostics, pviResult, file.name);

    // PVI store for graph panel
    setCompileResult(pviResult);

    // Real-time fee annotations
    const contractName = file.name.replace(/\.mist$/, '');
    const { lineAnnotations } = analyzeFees(content, contractName);
    applyFeeDecorations(editorRef.current, monacoRef.current, lineAnnotations);
  }, [isMist, file.name, setDiagnostics, setAnalyzing, setCompileResult]);

  const debouncedValidation = useCallback((content: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runValidation(content), 300);
  }, [runValidation]);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const handleBeforeMount: BeforeMount = useCallback((monaco) => {
    if (!mistRegistered) {
      registerMistLanguage(monaco);
      registerMistProviders(monaco);
      mistRegistered = true;
    }
  }, []);

  const handleMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    editor.addAction({
      id: 'rivellum-save',
      label: 'Save',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
      run: () => { onSave(); },
    });

    // Format on Shift+Alt+F for .mist files
    if (isMist) {
      editor.addAction({
        id: 'mist-format',
        label: 'Format Mist Document',
        keybindings: [monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF],
        run: () => { editor.getAction('editor.action.formatDocument')?.run(); },
      });

      // Track cursor position for source→graph linking
      editor.onDidChangeCursorPosition((e) => {
        const line = e.position.lineNumber;
        const { compileResult } = usePviStore.getState();
        if (compileResult.graph) {
          const node = findNodeAtLine(compileResult.graph, line);
          setHighlightedNodeId(node ? node.id : null);
        }
      });
    }

    editor.focus();

    // Run initial validation for .mist files
    if (isMist) {
      runValidation(file.content);
    }
  }, [onSave, isMist, file.content, runValidation]);

  const handleChange = useCallback((value: string | undefined) => {
    const content = value ?? '';
    onChange(content);
    debouncedValidation(content);
  }, [onChange, debouncedValidation]);

  return (
    <Editor
      height="100%"
      language={file.language}
      value={file.content}
      theme="rivellum-dark"
      beforeMount={handleBeforeMount}
      onChange={handleChange}
      onMount={handleMount}
      options={{
        fontSize: 14,
        fontFamily: "var(--font-mono), 'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
        fontLigatures: true,
        minimap: { enabled: true, scale: 1 },
        scrollBeyondLastLine: false,
        wordWrap: 'off',
        automaticLayout: true,
        tabSize: isMist ? 4 : 2,
        insertSpaces: true,
        detectIndentation: !isMist,
        renderWhitespace: 'selection',
        bracketPairColorization: { enabled: true },
        guides: { bracketPairs: true, indentation: true },
        cursorBlinking: 'smooth',
        cursorSmoothCaretAnimation: 'on',
        smoothScrolling: true,
        acceptSuggestionOnEnter: 'off',
        padding: { top: 8 },
      }}
    />
  );
}
